import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import LandingVideo from '@src/components/DisposalPage/LandingVideo'
import HexagonFeatures from '@src/components/DisposalPage/HexagonFeatures'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import solarClients from '@src/constants/solarClients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/junk-removal-page.module.css'
// eslint-disable-next-line import/extensions
import solar from '../../json/solar-panel-disposal-data.json'

const solarServiceData = {
  name: 'Solar Panel Disposal and Recycling Services',
  description:
    'Nationwide solar panel disposal and recycling for homes, businesses, and energy projects. Dimension collects old, damaged, and decommissioned panels, batteries, and inverters and routes them to certified recycling and hazardous-waste facilities, with rebates for functional equipment.',
  serviceType: 'Solar Panel Disposal and Recycling',
  url: 'https://getdimension.com/solar-panel-disposal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Solar Panel Recycling' },
    { name: 'Solar Panel Removal' },
    { name: 'Solar Battery Disposal' },
    { name: 'Inverter and Energy Storage Disposal' },
    { name: 'Commercial and Utility-Scale Solar Disposal' },
  ],
}

const solarFAQData = {
  questions: solar.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function SolarPanelDisposal() {
  return (
    <>
      <Head>
        <title>Solar Panel Disposal &amp; Recycling Services | Dimension</title>
        <meta
          name="description"
          content="Nationwide solar panel disposal and recycling. Recycle damaged panels from around $5 each, earn a rebate on functional equipment, and get certified transport and documentation."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/solar-panel-disposal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={solarServiceData} />
        <SchemaMarkup type="FAQPage" data={solarFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="solar"
          heading={solar.pitch.heading}
          material={solar.pitch.material}
          pitchText={solar.pitch.pitchText}
          ctaLink={solar.pitch.ctaLink}
          ctaText={solar.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <LandingVideo service="solar-panel-disposal" />
        <Clients clients={solarClients} />
        <HexagonFeatures />
        <FeatureList {...solar.solutions} />
        <FeatureList {...solar.benefits} />
        <CaseStudy {...solar.caseStudy} />
        <Approach {...solar.approach} />
        <FAQ {...solar.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Solar Decommissioning',
              href: '/solar-decommissioning',
              description:
                'Commercial and utility-scale solar project decommissioning, uninstallation, and site cleanup.',
            },
            {
              label: 'Sell Used Solar Panels',
              href: '/solar-panel-sell',
              description:
                'Turn functional used and new panels and equipment into cash instead of paying to dispose of them.',
            },
            {
              label: 'E-Waste Management',
              href: '/ewaste',
              description:
                'Certified recycling for batteries, inverters, and the electronics that come with a solar system.',
            },
            {
              label: 'Sustainability',
              href: '/sustainability',
              description:
                'See how Dimension keeps materials out of the landfill and reports on your diversion.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default SolarPanelDisposal
